import {
  DriverLocationEntity,
  GeoCoord,
  GeofenceEntryShiftTimelineEvent,
  GeofenceEventEntity,
  GeofenceExitShiftTimelineEvent,
  isLocationEntityDriverLocation,
  isLocationEntityGeofenceEvent,
  isLocationEntityTransport,
  LocationHistoryEntity,
  LocationHistoryEvent,
  ShiftEndShiftTimelineEvent,
  ShiftEntity,
  ShiftStartShiftTimelineEvent,
  ShiftTimeline,
  ShiftTimelineEvent,
  TransportEntity,
  TransportShiftTimelineEvent
} from "@ots-frontend/ots-data";
import {lineString as turfLineString, bbox as turfBbox, BBox} from "@turf/turf";
import {ShiftLocationHistory, TransportRoute} from "../../../model/location-history";
import {transportLineColors} from "../../../theme/colors";

export const getLocationAtDttm = (lookupDate: Date | number, data: ShiftLocationHistory | undefined): GeoCoord | undefined => {
  let coordinates: GeoCoord | undefined = undefined;

  if (data) {

    const lookupDttm = lookupDate.valueOf()

    let firstEvent: LocationHistoryEvent | undefined = undefined;
    let lastEvent: LocationHistoryEvent | undefined = undefined;


    for (const entry of data.events.entries()) {
      const dttm = entry[0];
      const event = entry[1];

      if (dttm <= lookupDttm) {
        firstEvent = event;
      } else if (dttm > lookupDttm) {
        lastEvent = event;
        break;
      }
    }

    if (
      firstEvent &&
      firstEvent.createDttm &&
      firstEvent.coordinates &&
      lastEvent &&
      lastEvent.createDttm &&
      lastEvent.coordinates
    ) {
      const diff = lastEvent.createDttm - firstEvent.createDttm;
      if (diff > 0) {
        const percent =
          (lookupDttm.valueOf() - firstEvent.createDttm) / diff;
        const diffLat =
          lastEvent.coordinates.latCoord - firstEvent.coordinates.latCoord;
        const newLat = firstEvent.coordinates.latCoord + diffLat * percent;
        const diffLong =
          lastEvent.coordinates.longCoord - firstEvent.coordinates.longCoord;
        const newLong = firstEvent.coordinates.longCoord + diffLong * percent;

        coordinates = {
          latCoord: newLat,
          longCoord: newLong,
        };
      }
    }
  }

  return coordinates;
};

/**
 * Returns the valid driver locations from the location history.
 *
 * Note: If shiftId is set, only driver locations with that shift will be returned.
 *
 * @param locationHistory The location history
 * @param shiftId (optional) The primary key of the shift.
 */
const getDriverLocations = (
  locationHistory: LocationHistoryEntity,
  shiftId: string | undefined = undefined
): DriverLocationEntity[] => {
  const driverLocations : DriverLocationEntity[] = []
  if (shiftId) {
    driverLocations.push(...locationHistory.driverLocations.filter((dl) => dl.shiftId === shiftId))
  } else {
    driverLocations.push(...locationHistory.driverLocations)
  }

  return driverLocations.sort((a, b) => a.createDttm - b.createDttm);
};

/**
 * Returns the valid transports from the location history.
 *
 * Note: Valid transports are transports with a location, shift id, and create date.
 * If shiftId is set, only transports with that shift will be returned.
 *
 * @param locationHistory The location history
 * @param shiftId (optional) The primary key of the shift
 */
export const getTransports = (
  locationHistory: LocationHistoryEntity,
  shiftId: string | undefined = undefined
): TransportEntity[] => {
  if (shiftId) {
    return [
      ...locationHistory.transports.filter(
        (t) => t.coordinates && t.shiftId === shiftId && t.createDttm
      ),
    ];
  }

  return [
    ...locationHistory.transports.filter(
      (t) => t.coordinates && t.shiftId && t.createDttm
    ),
  ];
};

/**
 * Returns the valid geofence events from the location history.
 *
 * Note: Valid geofence events are geofence events with a location, shift id, and create date.
 * If shiftId is set, only transports with that shift will be returned.
 *
 * @param locationHistory The location history
 * @param shiftId (optional) The primary key of the shift
 */
export const getGeofenceEvents = (
  locationHistory: LocationHistoryEntity,
  shiftId: string | undefined = undefined
): GeofenceEventEntity[] => {
  if (shiftId) {
    return [
      ...locationHistory.geofenceEvents.filter(
        (ge) => ge.coordinates && ge.shiftId === shiftId && ge.inDttm
      ),
    ];
  }

  return [
    ...locationHistory.geofenceEvents.filter(
      (ge) => ge.coordinates && ge.shiftId && ge.inDttm
    ),
  ];
};

/**
 * Creates transport routes from location history events.
 *
 * This function assumes that the event array is already sorted by date asc.
 *
 * @param events The location history events (sorted by date asc)
 */
export const getTransportRoutes = (
  events: LocationHistoryEvent[]
): TransportRoute[] => {
  const transportRoutes: TransportRoute[] = [];

  let lastTransport: TransportEntity | undefined;
  let transportCoordinates: GeoCoord[] = [];
  let transportDriverLocations: DriverLocationEntity[] = []
  let lastTransportCoordinates: GeoCoord[] | undefined;
  let lastTransportDriverLocations: DriverLocationEntity[] | undefined
  let colorCount = 0;

  events.forEach((event) => {
    if (event instanceof DriverLocationEntity) {
      transportCoordinates.splice(
        transportCoordinates.length,
        0,
        event.coordinates
      );

      transportDriverLocations.splice(
        transportDriverLocations.length,
        0,
        event
      );
    } else if (event instanceof TransportEntity) {
      lastTransport = event;
      transportCoordinates.splice(
        transportCoordinates.length,
        0,
        event.coordinates!
      );
      // Find the next route color
      const routeColor = transportLineColors[colorCount++];
      if (colorCount >= transportLineColors.length) {
        colorCount = 0;
      }
      // Add transport route
      transportRoutes.push({
        transport: event,
        route: transportCoordinates,
        driverLocations: transportDriverLocations,
        color: routeColor,
      });
      // Clean up and add data for last transport line
      lastTransportCoordinates = [...transportCoordinates];
      transportCoordinates = [];
      transportCoordinates.splice(
        transportCoordinates.length,
        0,
        event.coordinates!
      );

      lastTransportDriverLocations = [...transportDriverLocations]
      transportDriverLocations = []
    }
  });

  // Add last transport route
  if (
    lastTransport &&
    lastTransportCoordinates &&
    lastTransportCoordinates.length > 1
  ) {
    const routeColor = transportLineColors[colorCount > 0 ? colorCount - 1 : 0];
    transportCoordinates.forEach((coord) =>
      lastTransportCoordinates!.splice(
        lastTransportCoordinates!.length,
        0,
        coord
      )
    );
    // Add transport route
    transportRoutes.push({
      transport: lastTransport,
      route: lastTransportCoordinates,
      driverLocations: lastTransportDriverLocations ?? [],
      color: routeColor,
    });
  }

  return transportRoutes;
};

/**
 * Returns the location history map data for the shift.
 *
 * @param locationHistory The location history
 * @param shiftId The primary key of the shift
 */
export const getShiftLocationHistory = (
  locationHistory: LocationHistoryEntity,
  shiftId: string
): ShiftLocationHistory | undefined => {
  // Find the shift in the location history
  const shift = locationHistory.shifts.find((s) => s.id === shiftId);

  if (!shift) {
    // Shift was not found in the location history, so we return no data
    return undefined;
  }

  const transportIds: string[] = [];
  let minDttm: number | undefined = undefined
  let maxDttm: number | undefined = undefined;

  // Retrieve the shift specific events from the location history
  const validDriverLocations = getDriverLocations(locationHistory, shiftId);
  const validTransports = getTransports(locationHistory, shiftId);
  const validGeofenceEvents = getGeofenceEvents(locationHistory, shiftId);

  const eventMap: Map<number, LocationHistoryEvent> = new Map();

  // Add each event to the event map
  validDriverLocations.forEach((dl) => {
    eventMap.set(dl.createDttm, dl);

    if (!minDttm || dl.createDttm < minDttm) {
      minDttm = dl.createDttm
    }

    if (!maxDttm || dl.createDttm > maxDttm) {
      maxDttm = dl.createDttm;
    }
  });



  validTransports.forEach((t) => {
    const dttm = t.createDttm!

    eventMap.set(dttm, t);
    transportIds.push(t.id)

    if (!minDttm || dttm < minDttm) {
      minDttm = dttm
    }

    if (!maxDttm || dttm > maxDttm) {
      maxDttm = dttm;
    }
  });

  validGeofenceEvents.forEach((ge) => {
    const inDttm = ge.inDttm!
    const outDttm = ge.outDttm

    eventMap.set(inDttm, ge);

    if (!minDttm || inDttm < minDttm) {
      minDttm = inDttm
    }

    if (!maxDttm || inDttm > maxDttm) {
      maxDttm = inDttm;
    }

    if (outDttm) {
      if (!minDttm || outDttm < minDttm) {
        minDttm = outDttm
      }

      if (!maxDttm || outDttm > maxDttm) {
        maxDttm = outDttm;
      }
    }
  });

  // Sort the event map with date asc
  const sortedMap = new Map([...eventMap.entries()].sort());
  const sortedEvents = [...sortedMap.values()];

  // Get the transport routes
  const transportRoutes = getTransportRoutes(sortedEvents);

  const lineCoords : number[][] = transportRoutes.reduce((prev, curr) => {
    for (const geo of curr.route) {
      prev.push([geo.latCoord, geo.longCoord])
    }
    return prev
  }, [] as number[][])

  let bbox : BBox | undefined = undefined
  if (lineCoords.length > 0) {
    const line = turfLineString(lineCoords);
    bbox = turfBbox(line);
  }

  // Build the location history map data
  return {
    shift,
    transportRoutes,
    driverLocations: validDriverLocations,
    events: sortedMap,
    transportIds: transportIds,
    minDttm: minDttm,
    maxDttm: maxDttm,
    bbox: bbox,
  };
};

export const getShiftTimeline = (
  data: ShiftLocationHistory,
  selectedTransportIds: string[] | undefined = undefined
): ShiftTimeline => {
  const timelineEventMap: Map<number, ShiftTimelineEvent> = new Map();

  // First get the shift start and end events
  timelineEventMap.set(
    data.shift.startDttm.valueOf(),
    toShiftStartTimelineEvent(data.shift)
  );

  if (data.shift.endDttm) {
    timelineEventMap.set(
      data.shift.endDttm.valueOf(),
      toShiftEndTimelineEvent(data.shift)
    );
  }

  // convert location history events to timeline events
  data.events.forEach((event) => {
    if (event instanceof TransportEntity) {
      const isSelected = !selectedTransportIds || selectedTransportIds.findIndex((id) => event.id === id) !== -1;
      const route = data.transportRoutes.find(
        (tr) => tr.transport.id === event.id
      );
      const routeColor = route ? route.color : undefined;
      const timelineEvent = toTransportTimelineEvent(
        event,
        isSelected,
        routeColor
      );
      timelineEventMap.set(timelineEvent.date, timelineEvent);
    } else if (event instanceof GeofenceEventEntity) {
      const entryTimelineEvent = toGeofenceEntryTimelineEvent(event);
      timelineEventMap.set(
        entryTimelineEvent.date,
        entryTimelineEvent
      );

      if (event.outDttm) {
        const exitTimelineEvent = toGeofenceExitTimelineEvent(event);
        timelineEventMap.set(
          exitTimelineEvent.date,
          exitTimelineEvent
        );
      }
    }
  });

  const sortedMap = new Map([...timelineEventMap.entries()].sort());
  const sortedEvents = [...sortedMap.values()];

  return {
    events: sortedEvents,
  };
};

const toTransportTimelineEvent = (
  transport: TransportEntity,
  isSelected: boolean,
  routeColor?: string
): TransportShiftTimelineEvent => {
  return {
    id: transport.id,
    transportType: transport.type,
    quantity: transport.quantityDecimal,
    unitOfMeasure: transport.quantityUnitOfMeasure,
    siteId: transport.siteId,
    siteName: transport.siteName,
    waterTypeName: transport.waterTypeName,
    serviceTypeId: transport.serviceTypeId,
    serviceTypeName: transport.serviceTypeName,
    date: transport.createDttm!,
    selected: isSelected,
    routeColor: routeColor,
    type: 'TRANSPORT',
  };
};

const toGeofenceEntryTimelineEvent = (
  geofenceEvent: GeofenceEventEntity
): GeofenceEntryShiftTimelineEvent => {
  return {
    id: geofenceEvent.id,
    siteId: geofenceEvent.siteId,
    siteName: geofenceEvent.siteName,
    date: geofenceEvent.inDttm,
    type: 'GEOFENCE_ENTRY',
  };
};

const toGeofenceExitTimelineEvent = (
  geofenceEvent: GeofenceEventEntity
): GeofenceExitShiftTimelineEvent => {
  return {
    id: geofenceEvent.id,
    siteId: geofenceEvent.siteId,
    siteName: geofenceEvent.siteName,
    date: geofenceEvent.outDttm!,
    type: 'GEOFENCE_EXIT',
  };
};

const toShiftStartTimelineEvent = (
  shift: ShiftEntity
): ShiftStartShiftTimelineEvent => {
  return {
    id: shift.id,
    date: shift.startDttm,
    type: 'SHIFT_START',
  };
};

const toShiftEndTimelineEvent = (
  shift: ShiftEntity
): ShiftEndShiftTimelineEvent => {
  return {
    id: shift.id,
    date: shift.endDttm!,
    type: 'SHIFT_END',
  };
};

export const extractTransports = (
  events: Map<number, LocationHistoryEvent>,
  ids: string[] | undefined = undefined
): TransportEntity[] => {
  const transports: TransportEntity[] = [];
  events.forEach((event, _dttm) => {
    if (
      isLocationEntityTransport(event) &&
      (!ids || ids.findIndex((id) => id === event.id) !== -1)
    ) {
      transports.push(event);
    }
  });
  return transports;
};

export const extractDriverLocations = (
  events: Map<number, LocationHistoryEvent>
): DriverLocationEntity[] => {
  const driverLocations: DriverLocationEntity[] = [];
  events.forEach((event, _dttm) => {
    if (isLocationEntityDriverLocation(event)) {
      driverLocations.push(event);
    }
  });
  return driverLocations;
};

export const extractGeofenceEvents = (
  events: Map<number, LocationHistoryEvent>
): GeofenceEventEntity[] => {
  const geofenceEvents: GeofenceEventEntity[] = [];
  events.forEach((event, _dttm) => {
    if (isLocationEntityGeofenceEvent(event)) {
      geofenceEvents.push(event);
    }
  });
  return geofenceEvents;
};
